/* ============================================================
   sa-quotation-form.js — お見積書フォーム
   Actions on submit:
     1. Validate inputs (name, email format, terms)
     2. Read cart via /cart.js — build cartId per Quotation API spec
     3. POST /apps/plaud-quotation/api/quotations
     4. On success: wait for Klaviyo subscription, track event, then redirect
     5. On failure: redirect with fallback token (if configured)
   ============================================================ */
   (function () {
    'use strict';

    var STRINGS = {
      errName:      '',
      errEmail:     '',
      errPhone:     '',
      errTerms:     '',
      errEmptyCart: '',
      errGeneral:   '',
      errRateLimit: '',
    };

    /* ── cartId helper (per API spec) ───────────────────── */
    function cartIdFromCart(cart) {
      var token = String(cart && cart.token ? cart.token : '').trim();
      if (!token) return null;
      if (token.indexOf('gid://shopify/Cart/') === 0) return token;
      return 'gid://shopify/Cart/' + token;
    }

    /* ── Allowed locale values per API spec ─────────────── */
    var ALLOWED_LOCALES = ['en', 'en-US', 'zh-CN', 'zh-TW', 'ja', 'de', 'fr', 'es', 'it', 'ko'];

    function resolveLocale() {
      var lang = (document.documentElement.lang || 'en').trim();
      for (var i = 0; i < ALLOWED_LOCALES.length; i++) {
        if (ALLOWED_LOCALES[i].toLowerCase() === lang.toLowerCase()) return ALLOWED_LOCALES[i];
      }
      /* loose match on language tag prefix, e.g. "zh" → "zh-CN" */
      var prefix = lang.split('-')[0].toLowerCase();
      for (var j = 0; j < ALLOWED_LOCALES.length; j++) {
        if (ALLOWED_LOCALES[j].toLowerCase().split('-')[0] === prefix) return ALLOWED_LOCALES[j];
      }
      return 'en';
    }

    function normalizeCurrencyCode(value) {
      var code = String(value || '').trim().toUpperCase();
      return /^[A-Z]{3}$/.test(code) ? code : '';
    }

    function applyCurrencyParam(params, fields) {
      var currency = normalizeCurrencyCode(fields && fields.currency);
      if (currency) params.set('currency', currency);
    }

    function rememberQuotationCurrency(token, currency) {
      var code = normalizeCurrencyCode(currency);
      if (!token || !code) return;
      try {
        localStorage.setItem('saQuotationCurrency:' + token, code);
        localStorage.setItem('lastQuotationCurrency', code);
      } catch (_) {
        /* localStorage can be unavailable in private browsing */
      }
    }

    function moneyAmountFromCents(value) {
      var cents = Number(value);
      return isNaN(cents) ? 0 : cents / 100;
    }

    function fixedTaxTotalsFromCart(cart) {
      var grandTotalCents = Math.max(0, Number(cart && cart.total_price) || 0);
      var subtotalCents = Math.round(grandTotalCents / 1.1);
      var taxCents = Math.max(0, grandTotalCents - subtotalCents);
      return {
        subtotal: moneyAmountFromCents(subtotalCents),
        taxTotal: moneyAmountFromCents(taxCents),
        grandTotal: moneyAmountFromCents(grandTotalCents),
        taxRate: 10,
        source: 'cart-fixed-tax',
      };
    }

    function discountTotalForCartItem(item) {
      var direct = Number(item && item.total_discount);
      if (!isNaN(direct) && direct > 0) return direct;

      var originalLine = Number(item && item.original_line_price);
      var finalLine = Number(item && (item.final_line_price != null ? item.final_line_price : item.line_price));
      if (!isNaN(originalLine) && !isNaN(finalLine) && originalLine > finalLine) {
        return originalLine - finalLine;
      }

      var originalUnit = Number(item && item.original_price);
      var finalUnit = Number(item && item.final_price);
      var quantity = Number(item && item.quantity) || 0;
      if (!isNaN(originalUnit) && !isNaN(finalUnit) && originalUnit > finalUnit && quantity > 0) {
        return (originalUnit - finalUnit) * quantity;
      }

      return 0;
    }

    function buildQuotationCartSnapshot(cart, options) {
      var currency = normalizeCurrencyCode(cart && cart.currency);
      var fixedTaxEnabled = !!(options && options.fixedTaxEnabled);
      var items = Array.isArray(cart && cart.items) ? cart.items : [];
      var lines = items.map(function (item) {
        var quantity = Number(item && item.quantity) || 0;
        var finalLinePrice = item && item.final_line_price != null ? item.final_line_price : item && item.line_price;
        var lineTotalCents = Number(finalLinePrice) || 0;
        var discountCents = discountTotalForCartItem(item);
        return {
          key: item && item.key || '',
          variantId: item && item.variant_id || '',
          title: item && (item.product_title || item.title) || '',
          sku: item && (item.sku || item.variant_title) || '',
          quantity: quantity,
          unitPrice: moneyAmountFromCents(item && (item.final_price != null ? item.final_price : item.price)),
          lineTotal: moneyAmountFromCents(lineTotalCents),
          discountTotal: moneyAmountFromCents(discountCents),
          _originalLineCents: Number(item && item.original_line_price) || (Number(item && item.original_price) || 0) * quantity,
          _lineTotalCents: lineTotalCents,
          _discountCents: discountCents,
        };
      });

      var cartDiscountCents = Number(cart && cart.total_discount) || 0;
      var lineDiscountCents = lines.reduce(function (sum, line) {
        return sum + (Number(line._discountCents) || 0);
      }, 0);
      var remainingDiscountCents = Math.max(0, cartDiscountCents - lineDiscountCents);
      if (remainingDiscountCents > 0 && lines.length) {
        var weightTotal = lines.reduce(function (sum, line) {
          return sum + Math.max(0, Number(line._originalLineCents) || 0);
        }, 0) || lines.length;
        var allocatedCents = 0;
        lines.forEach(function (line, index) {
          var allocation = index === lines.length - 1
            ? remainingDiscountCents - allocatedCents
            : Math.floor(remainingDiscountCents * (weightTotal === lines.length ? 1 : Math.max(0, Number(line._originalLineCents) || 0)) / weightTotal);
          allocatedCents += allocation;
          line._discountCents += allocation;
          line._lineTotalCents = Math.max(0, line._lineTotalCents - allocation);
          line.discountTotal = moneyAmountFromCents(line._discountCents);
          line.lineTotal = moneyAmountFromCents(line._lineTotalCents);
          line.unitPrice = line.quantity > 0 ? line.lineTotal / line.quantity : line.unitPrice;
        });
      }

      lines.forEach(function (line) {
        delete line._originalLineCents;
        delete line._lineTotalCents;
        delete line._discountCents;
      });

      return {
        currency: currency,
        totalPrice: moneyAmountFromCents(cart && cart.total_price),
        totalDiscount: moneyAmountFromCents(cart && cart.total_discount),
        totals: fixedTaxEnabled ? fixedTaxTotalsFromCart(cart) : null,
        lines: lines,
      };
    }

    function rememberQuotationCartSnapshot(token, cart, options) {
      if (!token || !cart) return;
      try {
        localStorage.setItem('saQuotationCart:' + token, JSON.stringify(buildQuotationCartSnapshot(cart, options)));
      } catch (_) {
        /* localStorage can be unavailable in private browsing */
      }
    }

    function rememberLastQuotationToken(token) {
      if (!token) return;
      try {
        localStorage.setItem('lastQuotationToken', token);
      } catch (_) {
        /* localStorage can be unavailable in private browsing */
      }
    }

    function getLastQuotationToken() {
      try {
        return localStorage.getItem('lastQuotationToken') || '';
      } catch (_) {
        return '';
      }
    }

    function buildQuotationRedirectUrl(base, token, fields) {
      var params = new URLSearchParams();
      if (token) params.set('token', token);
      if (fields.name)    params.set('name',    fields.name);
      if (fields.suffix)  params.set('suffix',  fields.suffix);
      if (fields.email)   params.set('email',   fields.email);
      if (fields.phone)   params.set('phone',   fields.phone);
      if (fields.address) params.set('address', fields.address);
      applyCurrencyParam(params, fields);
      return base + '?' + params.toString();
    }

    function mergeFormParamsIntoUrl(url, fields) {
      var qIndex = url.indexOf('?');
      var path   = qIndex >= 0 ? url.slice(0, qIndex) : url;
      var params = new URLSearchParams(qIndex >= 0 ? url.slice(qIndex + 1) : '');
      if (fields.name)    params.set('name',    fields.name);
      if (fields.suffix)  params.set('suffix',  fields.suffix);
      if (fields.email)   params.set('email',   fields.email);
      if (fields.phone)   params.set('phone',   fields.phone);
      if (fields.address) params.set('address', fields.address);
      applyCurrencyParam(params, fields);
      return path + '?' + params.toString();
    }

    function absoluteUrl(url) {
      if (!url) return '';
      try {
        return new URL(url, window.location.origin).href;
      } catch (_) {
        return String(url);
      }
    }

    function klaviyoCompanyIdFromKey(key) {
      var normalized = String(key || '').trim();
      var pkMatch = normalized.match(/^pk_([^_]+)_/);
      return pkMatch ? pkMatch[1] : normalized;
    }

    function readApiResponse(res) {
      return res.text().then(function (text) {
        var data = {};
        if (text) {
          try {
            data = JSON.parse(text);
          } catch (_) {
            data = {
              error: ('Quotation API returned non-JSON response: ' + text.replace(/\s+/g, ' ').slice(0, 240)).trim(),
            };
          }
        }
        return { res: res, data: data };
      });
    }

    /* ── Custom element ──────────────────────────────────── */
    class SaQuotationForm extends HTMLElement {
      connectedCallback() {
        var root = this;
        if (root.dataset.errName)      STRINGS.errName      = root.dataset.errName;
        if (root.dataset.errEmail)     STRINGS.errEmail     = root.dataset.errEmail;
        if (root.dataset.errPhone)     STRINGS.errPhone     = root.dataset.errPhone;
        if (root.dataset.errTerms)     STRINGS.errTerms     = root.dataset.errTerms;
        if (root.dataset.errEmptyCart) STRINGS.errEmptyCart = root.dataset.errEmptyCart;
        if (root.dataset.errGeneral)   STRINGS.errGeneral   = root.dataset.errGeneral;
        if (root.dataset.errRateLimit) STRINGS.errRateLimit = root.dataset.errRateLimit;

        var form = this.querySelector('.sa-qf__form');
        if (!form) return;
        this._storeSubmitOriginalText();
        this._resetSubmitState();

        if (!this._boundSubmit) this._boundSubmit = this._onSubmit.bind(this);
        form.removeEventListener('submit', this._boundSubmit);
        form.addEventListener('submit', this._boundSubmit);

        if (!this._boundPageShow) {
          this._boundPageShow = this._onPageShow.bind(this);
          window.addEventListener('pageshow', this._boundPageShow);
        }
      }

      disconnectedCallback() {
        var form = this.querySelector('.sa-qf__form');
        if (form && this._boundSubmit) form.removeEventListener('submit', this._boundSubmit);
        if (this._boundPageShow) {
          window.removeEventListener('pageshow', this._boundPageShow);
          this._boundPageShow = null;
        }
      }

      _onPageShow(e) {
        if (e.persisted) this._resetSubmitState();
      }

      _storeSubmitOriginalText() {
        var btn = this.querySelector('[data-submit-text]');
        if (!btn) return;
        this._submitButtonOriginalText = btn.dataset.submitText || btn.textContent || '';
      }

      _setSubmitLoading(btn) {
        if (!btn) return;
        if (!this._submitButtonOriginalText) this._storeSubmitOriginalText();
        btn.disabled    = true;
        btn.textContent = btn.dataset.submittingText || this._submitButtonOriginalText || '';
      }

      _resetSubmitState() {
        var btn = this.querySelector('[data-submit-text]');
        if (!btn) return;
        btn.disabled = false;
        btn.textContent = this._submitButtonOriginalText || btn.dataset.submitText || btn.textContent || '';
      }

      _onSubmit(e) {
        e.preventDefault();
        this._clearErrors();

        var nameEl   = this.querySelector('[name="name"]');
        var suffixEl = this.querySelector('[name="suffix"]');
        var emailEl  = this.querySelector('[name="email"]');
        var addrEl   = this.querySelector('[name="address"]');
        var phoneEl  = this.querySelector('[name="phone"]');
        var termsEl  = this.querySelector('[name="terms"]');
        var btn      = this.querySelector('[data-submit-text]');

        var name    = nameEl   ? nameEl.value.trim()   : '';
        var suffix  = suffixEl ? suffixEl.value         : '';
        var email   = emailEl  ? emailEl.value.trim()   : '';
        var address = addrEl   ? addrEl.value.trim()    : '';
        var phone   = phoneEl  ? phoneEl.value.trim()   : '';
        var terms   = termsEl  ? termsEl.checked         : false;
        var formFields = { name: name, suffix: suffix, email: email, phone: phone, address: address };

        /* ── Validation ─────────────────────────────────── */
        var valid = true;
        if (!name) {
          this._showError(nameEl, STRINGS.errName, true);
          valid = false;
        }
        if (!email || !this._validEmail(email)) {
          this._showError(emailEl, STRINGS.errEmail);
          valid = false;
        }
        if (!terms) {
          this._showError(termsEl, STRINGS.errTerms);
          valid = false;
        }
        if (!valid) return;

        /* ── Loading state ──────────────────────────────── */
        this._setSubmitLoading(btn);

        var self          = this;
        var apiBase       = (self.dataset.apiBase || '/apps/plaud-quotation/api').replace(/\/$/, '');
        var quotationPage = (self.dataset.quotationPage || '/pages/quotation').replace(/\?.*$/, '').replace(/\/$/, '');
        var fallbackToken = self.dataset.fallbackToken || '';
        var fixedTaxEnabled = self.dataset.fixedTaxEnabled === 'true';
        var cartContext   = null;
        var klaviyoSubscription = Promise.resolve();

        /* ── [DEV] 临时直跳模式：填了 dev_token 则跳过所有 API 调用 ── */
        var devToken = (self.dataset.devToken || '').trim();
        if (devToken) {
          window.location.href = buildQuotationRedirectUrl(quotationPage, devToken, formFields);
          return;
        }

        function resetBtn() {
          self._resetSubmitState();
        }

        function goFallback() {
          if (fallbackToken) {
            window.location.href = buildQuotationRedirectUrl(quotationPage, fallbackToken, formFields);
          } else {
            resetBtn();
            self._showGlobalError(STRINGS.errGeneral);
          }
        }

        function redirectAfterKlaviyo(url, quotationToken, draftOrderCheckoutUrl) {
          klaviyoSubscription
            .then(function () {
              return self._trackKlaviyoQuotationEvent(email, name, {
                quotationToken: quotationToken || '',
                quotationUrl: url,
                draftOrderCheckoutUrl: draftOrderCheckoutUrl || '',
                cart: cartContext,
              });
            })
            .then(function () {
              window.location.href = url;
            })
            .catch(function () {
              window.location.href = url;
            });
        }

        /* ── Step 1: fetch cart ─────────────────────────── */
        fetch('/cart.js', { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            if (!cart || !cart.item_count) {
              resetBtn();
              self._showGlobalError(STRINGS.errEmptyCart);
              return null; /* signal: abort chain */
            }

            var cartId = cartIdFromCart(cart);
            var title  = name + (suffix ? (' ' + suffix) : '');
            var iKey   = ('cart-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10) + '-' + (cart.token || '') + '-' + email).slice(0, 128);
            var cartCurrency = normalizeCurrencyCode(cart.currency);
            if (cartCurrency) formFields.currency = cartCurrency;
            cartContext = {
              rawCart:    cart,
              cartId:     cartId,
              cartToken:  cart.token || '',
              itemCount:  cart.item_count || 0,
              totalPrice: cart.total_price || 0,
              currency:   cartCurrency,
            };

            var payload = {
              title:            title,
              email:            email,
              cartId:           cartId,
              privacyAccepted:  true,
              termsAccepted:    true,
              locale:           resolveLocale(),
              idempotencyKey:   iKey,
            };
            if (phone) payload.phone = phone;

            klaviyoSubscription = email ? self._subscribeKlaviyo(email, name) : Promise.resolve();

            /* ── Step 2: POST to Quotation API ───────────── */
            return fetch(apiBase + '/quotations', {
              method:      'POST',
              headers:     { 'Content-Type': 'application/json' },
              credentials: 'same-origin',
              body:        JSON.stringify(payload),
            });
          })
          .then(function (res) {
            if (!res) return; /* empty-cart path already handled */
            return readApiResponse(res).then(function (result) {
              var data = result.data;
              if (!res.ok) {
                resetBtn();
                self._showGlobalError(self._apiErrorMsg(data));
                return;
              }

              /* ── Success: token returned ─────────────── */
              if (data.quotationToken) {
                rememberLastQuotationToken(data.quotationToken);
                rememberQuotationCurrency(data.quotationToken, formFields.currency);
                rememberQuotationCartSnapshot(data.quotationToken, cartContext && cartContext.rawCart, {
                  fixedTaxEnabled: fixedTaxEnabled,
                });
                var draftOrderCheckoutUrl = data.draftordercheckout_url
                  || data.draftOrderCheckoutUrl
                  || data.draft_order_checkout_url
                  || data.checkoutUrl
                  || '';
                redirectAfterKlaviyo(mergeFormParamsIntoUrl(
                  data.quotationUrl || buildQuotationRedirectUrl(quotationPage, data.quotationToken, {}),
                  formFields
                ), data.quotationToken, draftOrderCheckoutUrl);
                return;
              }

              /* ── Idempotent hit: token is empty string ── */
              if (data.idempotentHit) {
                var saved = getLastQuotationToken();
                if (saved) {
                  rememberQuotationCurrency(saved, formFields.currency);
                  rememberQuotationCartSnapshot(saved, cartContext && cartContext.rawCart, {
                    fixedTaxEnabled: fixedTaxEnabled,
                  });
                  redirectAfterKlaviyo(buildQuotationRedirectUrl(quotationPage, saved, formFields), saved, '');
                  return;
                }
              }

              resetBtn();
              self._showGlobalError(self._apiErrorMsg(data));
            });
          })
          .catch(function (err) {
            resetBtn();
            self._showGlobalError((err && err.message) || STRINGS.errGeneral);
          });
      }

      /* ── API error code → user-visible message ─────────── */
      _apiErrorMsg(data) {
        var code = String((data && data.code) || '').toUpperCase();
        if (code === 'TITLE_REQUIRED')              return STRINGS.errName;
        if (code === 'EMAIL_INVALID')               return STRINGS.errEmail;
        if (code === 'PHONE_INVALID'
         || code === 'PHONE_NUMBER_INVALID'
         || code === 'INVALID_PHONE')               return STRINGS.errPhone;
        if (code === 'CONSENT_REQUIRED')            return STRINGS.errTerms;
        if (code === 'CART_EMPTY')                  return STRINGS.errEmptyCart;
        if (code === 'CART_NOT_FOUND'
         || code === 'CART_ID_INVALID'
         || code === 'CART_FETCH_FAILED')           return STRINGS.errEmptyCart;
        if (code === 'RATE_LIMITED')                return STRINGS.errRateLimit;
        if (this._isPhoneErrorText(data && (data.error || data.message || data.detail))) return STRINGS.errPhone;
        if (data && data.error) return data.error;
        if (data && data.message) return data.message;
        if (data && data.detail) return data.detail;
        if (data && data.errors && data.errors[0]) {
          var firstError = data.errors[0];
          var firstErrorCode = String(firstError.code || firstError.title || '').toUpperCase();
          if (firstErrorCode === 'PHONE_INVALID'
           || firstErrorCode === 'PHONE_NUMBER_INVALID'
           || firstErrorCode === 'INVALID_PHONE')   return STRINGS.errPhone;
          var firstErrorText = firstError.detail || firstError.message || firstError.title;
          if (this._isPhoneErrorText(firstErrorText)) return STRINGS.errPhone;
          return firstErrorText || STRINGS.errGeneral;
        }
        return STRINGS.errGeneral;
      }

      _isPhoneErrorText(text) {
        return /phone(?:\s+number)?\s+(?:is\s+)?invalid|invalid\s+phone(?:\s+number)?/i.test(String(text || ''));
      }

      /* ── Klaviyo ─────────────────────────────────────── */

      _subscribeKlaviyo(email, fullName) {
        var apiKey = klaviyoCompanyIdFromKey(this.dataset.klaviyoApi || '');
        var listId = this.dataset.klaviyoList || '';
        if (!apiKey || !listId || !email) return Promise.resolve();

        var nameParts  = (fullName || '').trim().split(/\s+/);
        var firstName  = nameParts[0] || '';
        var lastName   = nameParts.slice(1).join(' ') || '';

        return fetch(
          'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(apiKey),
          {
            method:    'POST',
            headers:   { 'Content-Type': 'application/json', 'revision': '2023-12-15' },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: { email: email, first_name: firstName, last_name: lastName },
                    },
                  },
                },
                relationships: {
                  list: { data: { type: 'list', id: listId } },
                },
              },
            }),
            keepalive: true,
          }
        ).catch(function () { /* silent */ });
      }

      _trackKlaviyoQuotationEvent(email, fullName, context) {
        var apiKey = klaviyoCompanyIdFromKey(this.dataset.klaviyoApi || '');
        if (!apiKey || !email) return Promise.resolve();

        var nameParts = (fullName || '').trim().split(/\s+/);
        var firstName = nameParts[0] || '';
        var lastName  = nameParts.slice(1).join(' ') || '';
        var cart      = (context && context.cart) || {};
        var token     = (context && context.quotationToken) || '';
        var total     = Number(cart.totalPrice || 0) / 100;
        var estimateUrl = absoluteUrl((context && context.quotationUrl) || '');

        var profileAttributes = { email: email, first_name: firstName, last_name: lastName };
        var eventAttributes = {
          properties: {
            source: 'sa-quotation-form',
            quotationToken: token,
            quotationUrl: estimateUrl,
            Estimate_url: estimateUrl,
            draftordercheckout_url: (context && context.draftOrderCheckoutUrl) || '',
            cartId: cart.cartId || '',
            cartToken: cart.cartToken || '',
            itemCount: cart.itemCount || 0,
          },
          metric: {
            data: {
              type: 'metric',
              attributes: { name: 'plaud-quotation' },
            },
          },
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes,
            },
          },
          time: new Date().toISOString(),
          value: total,
        };
        if (cart.currency) eventAttributes.value_currency = cart.currency;
        if (token) eventAttributes.unique_id = 'quotation-' + token;

        return fetch(
          'https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(apiKey),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'revision': '2023-12-15' },
            body: JSON.stringify({
              data: {
                type: 'event',
                attributes: eventAttributes,
              },
            }),
            keepalive: true,
          }
        ).catch(function () { /* silent */ });
      }

      /* ── Helpers ─────────────────────────────────────── */

      _validEmail(v) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
      }

      /* nameRow=true → mark the enclosing .sa-qf__name-row instead of the input */
      _showError(field, msg, nameRow) {
        if (!field) return;

        field.setAttribute('aria-invalid', 'true');

        var group = field.closest('.sa-qf__field-group');
        if (!group) return;

        if (nameRow) group.classList.add('sa-qf__field-group--error');

        var err = document.createElement('span');
        err.className = 'sa-qf__error';
        err.setAttribute('role', 'alert');
        err.textContent = msg;
        group.appendChild(err);
      }

      _showGlobalError(msg) {
        var el = this.querySelector('.sa-qf__global-error');
        if (!el) return;
        el.textContent = msg;
        el.hidden = false;
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      _clearErrors() {
        this.querySelectorAll('.sa-qf__error').forEach(function (e) { e.remove(); });
        this.querySelectorAll('[aria-invalid]').forEach(function (e) {
          e.removeAttribute('aria-invalid');
        });
        this.querySelectorAll('.sa-qf__field-group--error').forEach(function (e) {
          e.classList.remove('sa-qf__field-group--error');
        });
        var global = this.querySelector('.sa-qf__global-error');
        if (global) global.hidden = true;
      }
    }

    if (!customElements.get('sa-quotation-form')) {
      customElements.define('sa-quotation-form', SaQuotationForm);
    }
  })();
